import React, { useCallback, useMemo, useState } from 'react';

import { link } from '@aline/routes';
import type { Reference } from '@apollo/client';
import type { GridColDef, GridSortModel } from '@mui/x-data-grid-premium';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import type { ReportGridRow } from 'src/components';
import {
  AlineCheckbox,
  allContractTypeFilters,
  allLabelFilters,
  ContractTypeCell,
  DocumentLabelCell,
  documentListButtonId,
  DocumentsListFooter,
  documentStatusFilterOperator,
  getStringOperatorsWithSafeIsAnyOf,
  GridStatusCell,
  ListEmpty,
  ReportFiltersPanel,
  WorkflowCell,
} from 'src/components';
import { GridAlineTooltip } from 'src/components/generic/data-grid/grid-aline-tooltip';
import { DocumentIcon } from 'src/components/icons/DocumentIcon';
import { IconColors } from 'src/components/icons/icon-utils';
import type { Apollo } from 'src/gql/generated';
import {
  getIsBrowserHandledLinkClick,
  openLink,
  overflowMenuCSSId,
  partyTypename,
  usePartyParams,
} from 'src/helpers';
import {
  createDataGridPremiumPaperStyles,
  FiltersAnchorProvider,
  useFiltersAnchorState,
  useGetPartyDocuments,
} from 'src/hooks';
import type { UpdateCacheOnTrashArgs } from 'src/hooks';
import type { LabelSubset } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import { PartyDocumentNameCell } from './PartyDocumentNameCell';
import { PartyDocumentsToolbar } from './PartyGridToolbar';

const useStyles = makeStyles()(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
    position: 'relative',
  },
  emptyIcon: {
    height: 32,
    width: 32,
  },
  hoverEffectRow: {
    backgroundColor: theme.appColors.selectedBackground,
  },
  xGrid: {
    '& .MuiDataGrid-row': {
      [`&:hover #${documentListButtonId}`]: {
        visibility: 'visible',
      },
      [`&:hover #${overflowMenuCSSId}`]: {
        visibility: 'visible',
      },
    },
    flexGrow: 1,
    minHeight: 0, // Allow shrinking
  },
}));

interface DocumentRow extends Pick<
  Apollo.Document,
  | 'coreAiPropertiesExtractionFailedAt'
  | 'coreAiPropertiesExtractionFinishedAt'
  | 'coreAiPropertiesExtractionStartedAt'
  | 'id'
  | 'isCommunityTemplate'
  | 'name'
  | 'requesterFavorited'
  | 'requesterRole'
  | 'status'
  | '__typename'
  | 'variant'
> {
  labels: LabelSubset[];
  latestVersion: ReportGridRow['latestVersion'];
  owner: Pick<Apollo.User, 'id' | '__typename'>;
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
  workflow: Pick<Apollo.Workflow, 'id' | 'name' | '__typename' | 'variant'> | null;
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type DocumentGridDef<CellValue, FormattedValue = any> = GridColDef<
  DocumentRow,
  CellValue,
  FormattedValue
>;

export function PartyDocuments(): JSX.Element {
  const { classes } = useStyles();
  const { partyId } = usePartyParams();
  const { documents, loading } = useGetPartyDocuments(partyId, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'name' satisfies keyof DocumentRow, sort: 'asc' },
  ]);
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const updateCacheOnTrash = useCallback(
    ({ cache, trashedDocumentIds }: UpdateCacheOnTrashArgs) => {
      cache.modify({
        fields: {
          documents: (refs: readonly Reference[] = [], { readField }) => {
            return refs.filter((ref: Reference) => {
              const id = readField<UUID>('id', ref);
              return id && !trashedDocumentIds.has(id);
            });
          },
        },
        id: cache.identify({ __typename: partyTypename, id: partyId }),
      });
    },
    [partyId]
  );
  const columnDefs: [
    DocumentGridDef<DocumentRow['name']>,
    DocumentGridDef<DocumentRow['status']>,
    DocumentGridDef<DocumentRow['type']>,
    DocumentGridDef<DocumentRow['workflow']>,
    DocumentGridDef<DocumentRow['labels']>,
  ] = useMemo(() => {
    return [
      {
        field: 'name',
        filterOperators: getStringOperatorsWithSafeIsAnyOf(),
        flex: 1,
        headerName: 'Document Name',
        renderCell: PartyDocumentNameCell,
        type: 'string',
      },
      {
        field: 'status',
        filterOperators: [documentStatusFilterOperator],
        flex: 0,
        headerName: 'Status',
        minWidth: 120,
        renderCell: GridStatusCell,
        type: 'string',
      },
      {
        field: 'type',
        filterOperators: allContractTypeFilters,
        flex: 0,
        headerName: 'Type',
        minWidth: 200,
        renderCell: ContractTypeCell,
        type: 'string',
      },
      {
        field: 'workflow',
        filterable: false,
        flex: 0,
        headerName: 'Workflow',
        minWidth: 120,
        renderCell: WorkflowCell,
        type: 'string',
      },
      {
        field: 'labels',
        filterOperators: allLabelFilters,
        flex: 1,
        headerName: 'Labels',
        minWidth: 200,
        renderCell: DocumentLabelCell,
        type: 'string',
      },
    ];
  }, []);

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={classes.container}>
        <DataGridPremium<DocumentRow>
          checkboxSelection
          disableColumnMenu
          disableColumnResize
          disableRowSelectionOnClick
          showToolbar
          className={classes.xGrid}
          columns={columnDefs}
          filterMode="client"
          getRowHeight={() => 'auto'}
          loading={loading}
          rows={documents}
          slotProps={{
            filterPanel: {
              filterableColumns: 'visible',
            },
            footer: {
              totalCount: documents.length,
              totalDocumentCount: 'no-select-all',
              updateCacheOnTrash,
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            baseTooltip: GridAlineTooltip,
            filterPanel: ReportFiltersPanel,
            footer: DocumentsListFooter,
            noRowsOverlay: PartyDocumentsEmpty,
            toolbar: PartyDocumentsToolbar,
          }}
          sortModel={sortModel}
          sx={createDataGridPremiumPaperStyles}
          onRowClick={({ id, row }, event) => {
            /**
             * A modifier or middle click is a request for a new browser tab. The row background is
             * not a link, so honouring it here would navigate the current tab instead — do nothing
             * and let the row's name link be the thing that opens in a new tab.
             */
            if (getIsBrowserHandledLinkClick(event)) return;

            openLink(
              link.DocumentDetail({ documentId: String(id) }),
              row.name || 'Untitled Document'
            );
          }}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

function PartyDocumentsEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<DocumentIcon className={classes.emptyIcon} fill={IconColors.LightGray} />}
      subtitle="Adjust your search or add parties to documents."
      title="No documents"
    />
  );
}
